import React, { useMemo, useState } from 'react';
import { getMaxRotateForImage } from './decalEngine';

/**
 * User-editable image array for Blood Splatters.
 * Clean root-relative URLs pointing to local artwork assets.
 */
export const BLOOD_SPLATTER_IMAGES: (string | { src: string; maxRotate?: number })[] = [
  '/Images/Blood Splatters/1.png',
  '/Images/Blood Splatters/2.png',
  '/Images/Blood Splatters/3.png',
  '/Images/Blood Splatters/4.png',
  '/Images/Blood Splatters/5.png'
];

export interface BloodSplatterProps {
  x?: number; // percentage (0 - 100)
  y?: number; // percentage (0 - 100)
  rotation?: number; // degrees
  /** Custom +/- max rotation clamp (0 - 360). Defaults to 15 for liquid gravity physics */
  maxRotate?: number;
  scale?: number;
  opacity?: number;
  imageSrc?: string;
  images?: (string | { src: string; maxRotate?: number })[];
  dropShadow?: string;
  size?: number | string;
  className?: string;
  style?: React.CSSProperties;
}

export const BloodSplatter: React.FC<BloodSplatterProps> = ({
  x = 50,
  y = 50,
  rotation = 0,
  maxRotate,
  scale = 1,
  opacity = 0.75,
  imageSrc,
  images,
  dropShadow,
  size = 180,
  className = '',
  style
}) => {
  const pool = images && images.length > 0 ? images : BLOOD_SPLATTER_IMAGES;
  const chosen = useMemo(() => {
    if (imageSrc) return { src: imageSrc, maxRotate };
    const idx = Math.floor(Math.random() * pool.length);
    const item = pool[idx];
    if (typeof item === 'string') {
      return { src: item, maxRotate };
    }
    return { src: item.src, maxRotate: item.maxRotate ?? maxRotate };
  }, [imageSrc, pool, maxRotate]);

  const [currentSrc, setCurrentSrc] = useState(chosen.src);

  // Subtle liquid wall drop shadow contouring transparent PNG silhouette
  const bloodFilter = dropShadow || 'drop-shadow(1px 1px 1.5px rgba(0, 0, 0, 0.28))';

  // Liquid gravity physics: defaults to max +/- 15 degrees tilt, or custom maxRotate
  const effectiveMaxRotate = useMemo(() => {
    return getMaxRotateForImage(currentSrc, chosen.maxRotate ?? maxRotate, 'blood');
  }, [currentSrc, chosen.maxRotate, maxRotate]);

  const normalized = ((rotation % 360) + 540) % 360 - 180;
  const clampedRotation = effectiveMaxRotate < 360
    ? Math.max(-effectiveMaxRotate, Math.min(effectiveMaxRotate, normalized))
    : rotation;

  return (
    <div
      className={`random-thing decal-blood ${className}`}
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-50%, -50%) rotate(${clampedRotation}deg) scale(${scale})`,
        opacity,
        pointerEvents: 'none',
        userSelect: 'none',
        mixBlendMode: 'multiply',
        filter: bloodFilter,
        ...style
      }}
    >
      <img
        src={currentSrc}
        alt="blood splatter"
        style={{
          display: 'block',
          width: typeof size === 'number' ? `${size}px` : size,
          height: 'auto',
          pointerEvents: 'none'
        }}
        onError={() => {
          // If relative or unencoded failed, try alternative format
          if (!currentSrc.startsWith('/') && !currentSrc.startsWith('http')) {
            setCurrentSrc(`/${currentSrc}`);
          }
        }}
      />
    </div>
  );
};

export default BloodSplatter;
